import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClientHeader from "../../components/Client/ClientHeader";
import { useAuth } from "../../context/AuthContext";
import { useMessages } from "../../context/MessagesContext";
import { getMessages, sendMessage, markRead } from "../../api/chatService";
import "../../components/Client/clientTheme.css";
import "./Messages.css";

const THREAD_POLL_INTERVAL_MS = 5000;

function getMessageTypePreviews(t) {
  return {
    2: t("photoPreview"),
    3: t("productPreview"),
    4: t("videoPreview"),
    5: t("orderPreview"),
    6: t("locationPreview"),
  };
}

function formatTime(value) {
  const date = new Date(value);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function conversationPreview(conversation, t) {
  if (conversation.lastMessageText) return conversation.lastMessageText;
  if (conversation.lastMessageType) return getMessageTypePreviews(t)[conversation.lastMessageType] || "...";
  return t("noMessagesYet");
}

export default function Messages() {
  const { t } = useTranslation(["messages", "common"]);
  const { isAuthenticated, openLoginModal } = useAuth();
  const { conversations, refresh } = useMessages();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const pollRef = useRef(null);
  const endRef = useRef(null);

  const selectedConversation = conversations.find((c) => c.conversationId === selectedId);

  const requestedConversationId = searchParams.get("conversationId");

  useEffect(() => {
    if (!requestedConversationId || !isAuthenticated) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedConversationId, isAuthenticated]);

  useEffect(() => {
    if (!requestedConversationId) return;
    const id = Number(requestedConversationId);
    if (conversations.some((c) => c.conversationId === id)) {
      openConversation(id);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedConversationId, conversations]);

  function loadThread(conversationId) {
    getMessages(conversationId)
      .then((result) => setMessages(result.items.slice().reverse()))
      .catch(() => {});
  }

  function openConversation(conversationId) {
    setSelectedId(conversationId);
    setThreadLoading(true);
    getMessages(conversationId)
      .then((result) => setMessages(result.items.slice().reverse()))
      .finally(() => setThreadLoading(false));
    markRead(conversationId).then(refresh);
  }

  useEffect(() => {
    if (!selectedId) return;
    pollRef.current = setInterval(() => loadThread(selectedId), THREAD_POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  async function handleSend(e) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !selectedId) return;

    setSending(true);
    try {
      const message = await sendMessage(selectedId, { messageText: text });
      setMessages((prev) => [...prev, message]);
      setDraft("");
      refresh();
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="client-messages-page">
      <ClientHeader />

      <div className="client-messages-content">
        <h1 className="client-messages-title">{t("common:messages")}</h1>

        {!isAuthenticated ? (
          <div className="client-messages-empty">
            <p>{t("loginPrompt")}</p>
            <button type="button" className="client-messages-login-button" onClick={openLoginModal}>
              {t("common:logIn")}
            </button>
          </div>
        ) : conversations.length === 0 ? (
          <div className="client-messages-empty">
            <p>{t("noConversationsYet")}</p>
          </div>
        ) : (
          <div className={`client-messages-layout ${selectedId ? "thread-open" : ""}`}>
            <div className="client-messages-list">
              {conversations.map((conversation) => (
                <button
                  type="button"
                  key={conversation.conversationId}
                  className={`client-conversation-row ${
                    conversation.conversationId === selectedId ? "active" : ""
                  }`}
                  onClick={() => openConversation(conversation.conversationId)}
                >
                  {conversation.storeLogoUrl ? (
                    <img src={conversation.storeLogoUrl} alt="" className="client-conversation-avatar" />
                  ) : (
                    <div className="client-conversation-avatar client-conversation-avatar-placeholder" />
                  )}
                  <div className="client-conversation-info">
                    <div className="client-conversation-top-row">
                      <span className="client-conversation-name">{conversation.storeName}</span>
                      {conversation.lastMessageAt && (
                        <span className="client-conversation-time">{formatTime(conversation.lastMessageAt)}</span>
                      )}
                    </div>
                    <div className="client-conversation-bottom-row">
                      <span className="client-conversation-preview">
                        {conversation.lastMessageIsMine ? t("youPrefix") : ""}
                        {conversationPreview(conversation, t)}
                      </span>
                      {conversation.unreadCount > 0 && (
                        <span className="client-conversation-badge">{conversation.unreadCount}</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="client-messages-thread">
              {!selectedConversation ? (
                <div className="client-messages-thread-placeholder">{t("selectConversationPrompt")}</div>
              ) : (
                <>
                  <div className="client-messages-thread-header">
                    <button
                      type="button"
                      className="client-messages-back-button"
                      onClick={() => setSelectedId(null)}
                    >
                      ← {t("common:back")}
                    </button>
                    <span className="client-messages-thread-name">{selectedConversation.storeName}</span>
                  </div>

                  <div className="client-messages-thread-body">
                    {threadLoading ? (
                      <div className="client-messages-thread-placeholder">{t("common:loading")}</div>
                    ) : (
                      messages.map((message) => (
                        <div
                          key={message.messageId}
                          className={`client-message-bubble ${message.isMine ? "mine" : ""}`}
                        >
                          {message.isDeleted ? (
                            <span className="client-message-deleted">{t("messageDeleted")}</span>
                          ) : message.messageType === 2 && message.attachmentUrl ? (
                            <img src={message.attachmentUrl} alt="" className="client-message-image" />
                          ) : message.messageText ? (
                            <span>{message.messageText}</span>
                          ) : (
                            <span>{getMessageTypePreviews(t)[message.messageType] || t("message")}</span>
                          )}
                          <span className="client-message-time">{formatTime(message.sentAt)}</span>
                        </div>
                      ))
                    )}
                    <div ref={endRef} />
                  </div>

                  <form className="client-messages-compose" onSubmit={handleSend}>
                    <input
                      type="text"
                      placeholder={t("typeMessagePlaceholder")}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      className="client-messages-compose-input"
                    />
                    <button type="submit" className="client-messages-compose-send" disabled={sending || !draft.trim()}>
                      {t("send")}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
